import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { Box } from '@mui/material';
import { ADMIN_PAGES_KEYS, NS } from '../../../../../i18n/constants/keys';
import { Tabs } from '../../../../../ui';
import { toast } from '../../../../../ui/toast';
import {
  useCreateAutoAssessmentMutation,
  useGetAutoAssessmentQuery,
} from '../../../../../utils/api_service/jobOpeningsApi';
import { RootState } from '../../../../../utils/redux_store/store';
import {
  Assessment,
  BasicInfoInputs,
} from '../../../../../utils/types/assessment_types';
import {
  AutoAssessment,
  JobDescription,
} from '../../../../../utils/types/JobOpenings';
import { SelectOptions } from '../../../../../utils/types/react_select_types';
import { PreScreenQuestions } from '../../../../assessment/view/view_assessment/PreScreenQuestions';
import { Questions } from '../../AssessmentQuestions/Containers';
import LoadingSpinner from '../../ui/LoaderSpinner';
import { BEIQuestions } from './TabContent/BEIQuestions';

export interface AutoAssessmentProps {
  autoAssessment: AutoAssessment;
  setAutoAssessment: React.Dispatch<React.SetStateAction<AutoAssessment>>;
  jobDescription: JobDescription;
  jobTitle: string;
  completedElement: () => void;
  jobTitleId: string;
}

export const AutoAssessmentTab = ({
  autoAssessment,
  setAutoAssessment,
  jobDescription,
  jobTitle,
  completedElement,
  jobTitleId,
}: AutoAssessmentProps) => {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation(NS.adminPages);
  const AA = ADMIN_PAGES_KEYS.jobOpenings.autoAssessTab;
  const [assessmentID, setAssessmentID] = useState<string>('');
  const [activeTab, setActiveTab] = useState('preScreenQuestions');

  const tabTitles = [
    { label: t(AA.preScreenQuestions), value: 'preScreenQuestions' },
    { label: t(AA.skillQuestions), value: 'listOfQuestions' },
    { label: t(AA.beiQuestions), value: 'behavioralQuestions' },
  ];

  const skills = useSelector((state: RootState) => state.skills);
  const levels = useSelector((state: RootState) => state.skillLevels);
  const jobTitles = useSelector((state: RootState) => state.jobTitles);
  const subSkills = useSelector((state: RootState) => state.subSkills);
  const [skillList, setSkillList] = useState<SelectOptions[]>(skills.skillsList);
  const [assessmentInfo, setAssessmentInfo] = useState<Assessment | undefined>(
    autoAssessment.assessment,
  );
  const [, setAssessmentFormInfo] = useState<BasicInfoInputs>({
    title: autoAssessment.assessment.assessmentTitle,
    description: autoAssessment.assessment.description,
    startDate: autoAssessment.assessment.startDate
      ? new Date(autoAssessment.assessment.startDate * 1000).toISOString().split('T')[0]
      : '',
    endDate: autoAssessment.assessment.endDate
      ? new Date(autoAssessment.assessment.endDate * 1000).toISOString().split('T')[0]
      : '',
    type: autoAssessment.assessment.assessmentType,
    jobPosition: autoAssessment.assessment.jobPosition ?? undefined,
  });

  const shouldFetchQuery = Boolean(id && autoAssessment.id && autoAssessment.id.length > 0);
  const { data: fetchedAssessment, isLoading: isQueryLoading } = useGetAutoAssessmentQuery(
    id || '',
    { skip: !shouldFetchQuery }
  );

  const [createAutoAssessmentMutation, { isLoading: isMutationLoading }] =
    useCreateAutoAssessmentMutation();

  const processAssessmentData = (data: AutoAssessment) => {
    setAutoAssessment(data);
    setAssessmentInfo(data.assessment);
    setAssessmentFormInfo({
      title: data.assessment.assessmentTitle,
      description: data.assessment.description,
      startDate: data.assessment.startDate
        ? new Date(data.assessment.startDate * 1000).toISOString().split('T')[0]
        : '',
      endDate: data.assessment.endDate
        ? new Date(data.assessment.endDate * 1000).toISOString().split('T')[0]
        : '',
      type: data.assessment.assessmentType,
      jobPosition: data.assessment.jobPosition ?? undefined,
    });
  };

  useEffect(() => {
    if (fetchedAssessment) {
      setAssessmentID(fetchedAssessment.assessment.id);
      processAssessmentData(fetchedAssessment);
    }
  }, [fetchedAssessment]);

  useEffect(() => {
    const createInitialAssessment = async () => {
      if (!id || (autoAssessment.id && autoAssessment.id.length > 0)) {
        return;
      }

      try {
        const data = await createAutoAssessmentMutation(autoAssessment).unwrap();
        setAssessmentID(data.assessment.id);
        processAssessmentData(data);
      } catch (error) {
        toast.error('Error Initializing Assessment', { position: 'bottom-center' });
      }
    };

    createInitialAssessment();
  }, [id]);

  const loading = (shouldFetchQuery && isQueryLoading) || isMutationLoading;

  const renderTabContent = () => {
    if (loading) {
      return <LoadingSpinner />;
    }
    if (activeTab === 'preScreenQuestions' && assessmentInfo) {
      return (
        <PreScreenQuestions
          assessmentInfo={assessmentInfo}
          setAssessmentInfo={setAssessmentInfo}
        />
      );
    } else if (activeTab === 'listOfQuestions') {
      return (
        <Box sx={{ my: { xs: 1.5, sm: 2.5 } }}>
          <Questions
            autoAssessment={autoAssessment}
            setAutoAssessment={setAutoAssessment}
            assessmentId={autoAssessment.assessment.id}
            skillList={skillList}
            levelList={levels.levelList}
            jobTitleList={jobTitles.jobTitlesList}
            subSkillList={subSkills.subSkillsList}
            setSkillList={setSkillList}
            jobTitle={jobTitle}
            jobTitleId={jobTitleId}
            completedElement={completedElement}
            skills={[
              ...(jobDescription?.desirableSkills || []),
              ...(jobDescription?.mandatorySkills || []),
            ]}
          />
        </Box>
      );
    } else if (activeTab === 'behavioralQuestions') {
      return (
        <Box sx={{ my: { xs: 1.5, sm: 2.5 } }}>
          <BEIQuestions
            assessmentID={assessmentID}
            jobDescription={jobDescription}
          />
        </Box>
      );
    }
    return null;
  };

  return (
    <Box sx={{ minWidth: 0 }}>
      <Box sx={{ mb: { xs: 1.5, sm: 2 } }}>
        <Tabs
          tabs={tabTitles}
          activeTab={activeTab}
          onTabChange={(tabValue) => {
            setActiveTab(tabValue);
          }}
        />
      </Box>
      {renderTabContent()}
    </Box>
  );
};
